import { set, map, forEach, reduce, times, cloneDeep, isEqual, last, orderBy } from 'lodash'

import moment from 'moment'
import CartItemViewModel from '../viewmodels/CartItemViewModel'
import { centsToDollars, centsToFloat } from './formatters'
import { isCreditType, STADIUM_ORDER_STATES } from './orderStates'
import { isCardPresentCardType, PAYMENT_TYPES_INTERNAL } from './paymentTypes'
import { getTenderInfo } from './orderDisplay'

const generateUuid = require('uuid/v4')

const ORDER_CREATION_PARAMS = [
  'clientCreatedAt',
  'standMenuUuid',
  'orderMenuItems',
  'orderNumber',
  'payments',
  'webOrder',
  'uuid',
  'affiliations',
  'tipAmountInCents',
  'charityAmountInCents',
  'userNotes',
  'employeeId',
  'userAttributes',
  'userName',
  'state',
  'promotions',
  'reason',
  'receiptSignature',
  'taxExempt',
  'taxExemptReason',
  'taxExemptApproverUuid',
  'cashRounded',
]

/**
 *
 * @param {Object} order the order object
 * @returns the request ID of the order that correlates with the card reader
 */
export const orderReplayRequestId = (order) => {
  // TODO: T42086 consolidate where we store requestID
  const cardReaderRequestId = order?.cardReaderData?.requestId
  if (cardReaderRequestId) {
    return cardReaderRequestId
  }

  // TODO: T42086 consolidate where we store requestID
  const freedompayInvoiceNumber =
    order?.userAttributes?.freedompayInvoiceNumber || order?.payments?.[0]?.userAttributes?.freedompayInvoiceNumber
  if (freedompayInvoiceNumber) {
    return freedompayInvoiceNumber
  }

  const offlinePaymentWithoutToken = order?.payments?.find?.((payment) => {
    return payment.userAttributes?.isOfflineApproval && !payment.userAttributes?.token
  })

  if (order?.isTab && offlinePaymentWithoutToken) {
    return offlinePaymentWithoutToken.userAttributes.freedompayRequestId
  }

  if (order?.isTab && order?.neededToBeVoidedAuthPayment) {
    return order?.neededToBeVoidedAuthPayment?.find((payment) => payment?.userAttributes?.isOfflineApproval)?.userAttributes
      ?.freedompayRequestId
  }

  return null
}

export const hasRequiredModifiers = (item) =>
  item?.modifierGroups?.find(({ requiresSelection, min, defaults }) => requiresSelection || min > 0 || defaults?.length)

export const getPaymentNames = (order, reopenedTabPayments) => {
  let paymentDisplay
  const issuer = createOrderParams(order)?.userAttributes?.freedompayIssuerName
  const lastFour = createOrderParams(order)?.userAttributes?.last_4
  const cardType = createOrderParams(order)?.payments?.cardType ?? ''
  const hasCreditCardInfo = issuer && lastFour
  const tenderResponse = getTenderInfo(order, reopenedTabPayments)
  const isSubmitted = createOrderParams(order)?.state === 'submitted' || createOrderParams(order)?.state === 'submission_failed'

  if (hasCreditCardInfo) {
    paymentDisplay = `${cardType} ${lastFour} ${issuer}`
  } else if (isSubmitted) {
    paymentDisplay = 'N/A'
  } else {
    paymentDisplay = order.displayName
  }

  if (!order?.tabName && !order?.userName) {
    return paymentDisplay || `${tenderResponse?.displayText}`
  }

  return `${tenderResponse?.displayText} • ${order?.tabName ?? order?.userName}`
}

export const getTabNamesByKeyword = (orders, keyword) => {
  return orders
    .filter((order) => order.isTab && (order.tabName?.includes(keyword) || order.userName?.includes(keyword)))
    .map((order) => order.tabName ?? order.userName)
}

/**
 * @deprecated No longer needed - For refunds, we are taking the payment information in refund mutation
 */
export const markPaymentsWhenIsCardPresentTransaction = (payments) => {
  const hasCardPresent = payments.some((payment) => {
    if (payment?.userAttributes?.isCardPresentTransaction) {
      return true
    }
    return false
  })

  return payments.map((payment) => {
    if (payment?.userAttributes === undefined) {
      payment.userAttributes = {}
    }

    return {
      ...payment,
      userAttributes: {
        ...payment.userAttributes,
        isCardPresentTransaction: hasCardPresent,
      },
    }
  })
}

// TODO(mkramerl): transform the existing order into what is needed here.
export const createOrderParams = (order) => {
  // TEMP : Checking for createOrderParams key to provide backwards compatiblitiy for approved offline
  // that were created before the createOrderParams refactor and are unable to be synced, Once these approved offline
  // orders in prod have been synced, this can be reverted back to only checking on the order object
  const orderParams = order?.createOrderParams ? order.createOrderParams : order

  // Filter the order map to include only the necessary fields.
  return Object.keys(orderParams || {}).reduce((acc, key) => {
    if (ORDER_CREATION_PARAMS.includes(key)) acc[key] = orderParams[key]

    return acc
  }, {})
}

/**
 * Maps card reader data to the current payment format
 *
 * @param {Object} cardData Card Reader Data
 * @param {Object} previousPayment Previous Payment data to merge with the new card data
 * @returns {Object} Mapped payment data
 */
export const mapCardReaderDataAsCurrentPayment = (cardData = {}, previousPayment = {}) => {
  const userAttributes = {
    ...cardData.receiptData,
    ...cardData.authResponseData,
    cardholderName: cardData.cardholderName,
    freedompayInvoiceNumber: cardData.invoiceNumber,
    freedompayRequestId: cardData.requestId,
    freedompayIssuerName: cardData.cardIssuer || cardData.cardType,
    freedompayMerchantReferenceCode: cardData.merchantReferenceCode,
    isCardPresentTransaction: isCardPresentCardType(cardData.cardType),
    last4: cardData.receiptData?.maskedCardNumber,
    last_4: cardData.receiptData?.maskedCardNumber,
    token: cardData.tokenInformation?.token,
  }

  return {
    paymentType: PAYMENT_TYPES_INTERNAL.CREDIT_CARD,
    ...previousPayment,
    cardType: cardData.cardType,
    shortDescription: `${cardData.cardIssuer} ${cardData?.receiptData?.maskedCardNumber}`,
    userAttributes: {
      ...previousPayment.userAttributes,
      ...userAttributes,
    },
  }
}

/**
 * Auth Card Reader Data Syncs for Submitted Orders
 *
 * @param {Object} order
 * @param {Object} cardData
 * @returns {Object} - Updated order object with card data
 */
export const mapCardReaderDataForSubmittedOrder = (order, cardData) => {
  if (cardData.reasonText === 'NOT FOUND') {
    return {
      ...order,
      state: STADIUM_ORDER_STATES.SUBMITTED,
      reason: 'No card transaction found during sync',
    }
  }
  const previousPayment = order?.payments?.[0] || {}
  const currentPayment = mapCardReaderDataAsCurrentPayment(cardData, previousPayment)

  return {
    ...order,
    payments: [currentPayment],
    last4: cardData?.receiptData?.maskedCardNumber,
    reason: 'Order synced with replay auth from submitted state',
    userAttributes: {
      ...order.userAttributes,
      ...currentPayment.userAttributes,
      reason: 'Order synced with replay auth from submitted state',
    },
  }
}

// The definitive entry point for determining if an order is approved offline.
export const getOrderIsApprovedOffline = (order) => {
  if (order?.state === STADIUM_ORDER_STATES.AUTHORIZATION_FAILED) {
    return false
  }
  if (createOrderParams(order)?.state === STADIUM_ORDER_STATES.TABBED_OFFLINE) {
    return false
  }

  return (
    createOrderParams(order)?.state === STADIUM_ORDER_STATES.APPROVED_OFFLINE ||
    (!order?.wasCreatedInStadium && createOrderParams(order)?.state !== STADIUM_ORDER_STATES.TABBED)
  )
}

// The definitive entry point for determining if an order is unsyncable.
export const getOrderIsUnsyncable = (order) => {
  return !order?.wasCreatedInStadium && !!order?.unsyncable
}

export const orderDate = (order) => {
  return order?.updatedAt || order?.modifiedAt || order?.submittedAt
}

const expandLineItems = (lineItems) => {
  const clonedLinedItems = cloneDeep(lineItems)

  const result = reduce(
    clonedLinedItems,
    (accumulator, lineItem) => {
      const clones = times(lineItem.quantity || 1, () => {
        const clone = cloneDeep(lineItem)
        clone.quantity = 1

        return clone
      })

      return [...accumulator, ...clones]
    },
    [],
  )

  return result
}

export const parseModifiers = (modifiers) => {
  const mappedModifiers = map(modifiers, (modifier) => {
    if (!modifier.lineItemUuid) {
      const modUuid = generateUuid()
      set(modifier, 'lineItemUuid', modUuid)
      set(modifier, 'uuid', modUuid)
    }

    return {
      menuItemUuid: modifier.id,
      quantity: 1,
      uuid: modifier.lineItemUuid,
      price: centsToDollars(modifier.defaultPriceInCents).slice(1),
      priceInCents: modifier.defaultPriceInCents,
    }
  })

  return [mappedModifiers]
}

// Makes general order lineItem data to be used locally
// sets lineItemUuids on each item and item modifier used for refunds.
export const makeLineItemData = (lineItems) => {
  const expandedLineItems = expandLineItems(lineItems)

  const itemModels = expandedLineItems.map((lineItem, index) => {
    // since the view model is memoizing, passing in index as the first attribute
    // to make sure they are all treated uniquely
    return CartItemViewModel({ ...index, ...lineItem })
  })

  forEach(itemModels, (item) => {
    set(item, 'lineItemUuid', generateUuid())
    const modifiers = item.modifiers || []

    item.modifiers = modifiers.map((modifier) => ({
      ...modifier,
      lineItemUuid: generateUuid(),
    }))
  })

  return itemModels
}

// Create orderMenuItems data for platform order creation
export const makeCreateOrderLineItems = (itemModels = []) => {
  return itemModels.map((item) => {
    const modifiers = parseModifiers(item.modifiers)

    const obj = {
      uuid: item.lineItemUuid,
      menuItemUuid: item.id,
      quantity: item.quantity ?? 1,
      price: +centsToFloat(item.priceInCents),
      modifiers,
      notes: item.notes,
    }

    if (item.userAttributes) {
      obj.userAttributes = item.userAttributes
    }

    if (item.openPrice) {
      obj.reason = item.reason || ''
      obj.approverUuid = item.managerId
      obj.name = item.name
    }

    return obj
  })
}

export const getUserNameFromCard = (cardReaderData) => {
  const name = cardReaderData.cardholderName || '/'
  const invalidNameRegex = /\S*\d+\S*/g

  if (invalidNameRegex.test(name)) {
    return ''
  }

  // example: "OBERG/PETER               "
  // super defensive with the string methods below because I don't know much about different possibilities
  const split = name.split('/')
  const firstName = (split[1] || '').trim()
  const lastName = (split[0] || '').trim()
  const fullName = `${firstName} ${lastName}`
  const result = fullName.trim()

  return result
}

// called user_attributes in stadium, but it mostly contains credit card attributes
export const makeUserAttributes = (cardReaderData, internalPaymentType = null) => {
  const { authResponseData, receiptData } = cardReaderData

  const { authorizationCode, processorTransactionId, reconciliationId } = authResponseData || {}
  const { approvalCode, authStatus, maskedCardNumber, maskedPan } = receiptData || {}

  const safeFour = maskedCardNumber || ''
  const lastFour = safeFour.substring(safeFour.length - 4)

  return {
    last_4: lastFour,

    // needed in this format for refunds
    freedompayRequestId: cardReaderData.requestId,
    freedompayMerchantReferenceCode: cardReaderData.merchantReferenceCode || cardReaderData.uuid,
    freedompayInvoiceNumber: cardReaderData.invoiceNumber,
    freedompayIssuerName: cardReaderData.cardIssuer || cardReaderData.cardType,
    freedompayStoreId: cardReaderData.storeId,
    freedompayTerminalId: cardReaderData.terminalId,

    isCardPresentTransaction: isCardPresentCardType(cardReaderData.cardType),
    isOfflineApproval: cardReaderData.isOfflineApproval,

    // extra data to help troubleshoot customer charge back requests
    authorizationCode,
    processorTransactionId,
    reconciliationId,
    approvalCode,
    authStatus,
    maskedCardNumber,
    maskedPan,
    internalPaymentType,
    token: cardReaderData?.tokenInformation?.token,
  }
}

export const getModifiersWithItems = (itemModels) => {
  let totalInCents = 0
  let allItems = []

  itemModels.forEach((item) => {
    //TODO: T40311 Check to see where this priceInCents comes...
    let itemCost = item.priceInCents

    allItems.push({ ...item, price: centsToDollars(item.priceInCents) })

    if (item.modifiers) {
      item.totalModifiersWithCost = 0
      item.modifiers.forEach((modifier) => {
        itemCost += modifier.defaultPriceInCents
        modifier.priceInCents = modifier.defaultPriceInCents
        modifier.quantity = 1
        modifier.fractionalQuantity = 1
        item.totalModifiersWithCost++
        allItems.push({ ...modifier, price: centsToDollars(modifier.defaultPriceInCents) })
      })
    }

    totalInCents += itemCost
  })

  return {
    items: allItems,
    totalCostInCents: totalInCents,
  }
}

export const nonZeroDisplayAmount = (displayAmount) => !!displayAmount && displayAmount !== '$0.00'

export const numberAsCurrency = (floatAmount) => (floatAmount ? '$' + floatAmount.toFixed(2).toString() : '$0.00')

export const taxExemptable = (lineItem) => {
  // TODO(westinschepper): It seems dangerous to assume their is only one tax rule.
  const taxRule = lineItem?.taxRules?.[0] ?? {}

  return taxRule.exempt ?? true
}

export const getServiceChargeLineItem = (lineItems) => {
  return lineItems?.find?.((lineItem) => ['service_charge', 'taxable_service_charge'].includes(lineItem?.specialType))
}

export const cardReaderApproved = (cardReaderData) => {
  const status = cardReaderData?.status
  const approvalStatus = cardReaderData?.approvalStatus

  return status === 'ACCEPT' || status === 'APPROVED OFFLINE' || approvalStatus === 'ACCEPT'
}

export const getCardDataMessage = (data) =>
  [data?.reasonCode, data?.reasonText, data?.message].filter((str) => str?.length).join(', ')

export const orderSummaryKeys = [
  'gross',
  'tips',
  'discounts',
  'refunds',
  'net',
  'pendingRefundCount',
  'cash',
  'creditCard',
  'virtualCurrency',
  'tax',
]

export const inValidOrderStates = ['submitted', 'submission_failed', 'authorization_failed']

export const getIsPreAuthTab = (order) =>
  order?.cardReaderData?.transType === 'auth' || order?.userAttributes?.transType === 'auth'

/**
 * This function takes an order and groups the items together by id and modifiers
 *
 * @param {*} order
 * @returns an array of items with quantities grouped together
 */

export const getGroupedItems = (order) => {
  const clonedOrder = cloneDeep(order)
  const itemModels = clonedOrder.itemModels || []

  return itemModels.reduce((accumulator, item) => {
    const idsMatch = isEqual((last(accumulator) || {}).id, item.id)
    const modifiersMatch = isEqual((last(accumulator) || {}).modifiers, item.modifiers)

    //Open price matchings => for open price items item uuid will be the same but prices can be different so we need to add
    //price and name to the matching algorithm or everywhere the items are grouped will break
    const priceMatch = isEqual((last(accumulator) || {}).price, item.price)
    const nameMatch = isEqual((last(accumulator) || {}).name, item.name)

    if (item.refund) return accumulator

    if (idsMatch && modifiersMatch && priceMatch && nameMatch) {
      last(accumulator).quantity += 1
      //for price adjustment we need to keep track of the lineItemUuids being grouped together
      last(accumulator).lineItemUuids.push(item.lineItemUuid)
    } else {
      item.lineItemUuids = [item.lineItemUuid]
      accumulator.push(item)
    }

    return accumulator
  }, [])
}

/**
 * This function takes an order and groups the refunded items together by id and modifiers
 *
 * @param {*} order
 * @returns an array of items with quantities grouped together
 */

export const getGroupedRefundedItems = (order) => {
  const clonedOrder = cloneDeep(order)
  const itemModels = clonedOrder.refundedItemModels || []

  return itemModels.reduce((accumulator, item) => {
    const idsMatch = isEqual((last(accumulator) || {}).id, item.id)
    const modifiersMatch = isEqual((last(accumulator) || {}).modifiers, item.modifiers)
    const hasRefundedModifier = item.modifiers?.some((modifier) => modifier.refund) || false

    const addOrIncrement = (item) => {
      if (idsMatch && modifiersMatch) {
        last(accumulator).quantity += 1
        return
      }
      accumulator.push(item)
    }

    //Need to add the item if it is a refund
    if (item.refund) {
      addOrIncrement(item)
      return accumulator
    }

    //if item was not refunded but has a refunded modifier we need to count it so the modifier displays in the receipt
    if (hasRefundedModifier) {
      const newItem = {
        ...item,
        price: '$0.00',
        priceInCents: 0,
      }
      addOrIncrement(newItem)
    }

    return accumulator
  }, [])
}

export const hasOfflineCardTransactions = (order) => {
  const payments = order?.payments || []
  const orderIsOfflineApproval = order?.userAttributes?.isOfflineApproval

  return payments.some(
    (payment) => isCreditType(payment?.paymentType) && (payment.userAttributes?.isOfflineApproval || orderIsOfflineApproval),
  )
}

/**
 * Distributes the tip amount equally among all payments except the last one
 *
 * @param {Number} tipAmountInCents
 * @param {Array} payments
 * @returns {Array}
 */
export const distributeTipAmount = (tipAmountInCents = 0, payments = [], includeTipInTotal = false) => {
  const paymentsWithCharge = payments.reduce((acc, payment) => {
    if (payment.amountInCents === 0) {
      return acc
    }
    acc += 1
    return acc
  }, 0)

  const tipPerPayment = Math.floor(tipAmountInCents / paymentsWithCharge)
  let remainingTipInCents = tipAmountInCents

  return payments.map((payment, index) => {
    // if it's last payment
    if (index === payments.length - 1) {
      const amountInCents = includeTipInTotal ? payment.amountInCents + remainingTipInCents : payment.amountInCents
      return { ...payment, tip: remainingTipInCents, amountInCents }
    }

    if (payment.amountInCents === 0) {
      return { ...payment, tip: 0 }
    }

    const amountInCents = includeTipInTotal ? payment.amountInCents + tipPerPayment : payment.amountInCents
    remainingTipInCents -= tipPerPayment
    return { ...payment, tip: tipPerPayment, amountInCents }
  })
}

/**
 * Accumulates all unique freedompayInvoiceNumber values from order payments into
 * order.userAttributes.relatedInvoiceNumbers as an array.
 *
 * This preserves the complete history of invoice numbers even when payments are
 * removed or userAttributes is cleared for multi-payment scenarios.
 *
 * @param {Object} order - The order object to process
 * @returns {Object} Clone of order with updated relatedInvoiceNumbers
 */
export const accumulateRelatedInvoiceNumbers = (order) => {
  if (!order) return order

  // Extract from existing relatedInvoiceNumbers into a Set for uniqueness
  const invoiceNumbers = new Set(order.userAttributes?.relatedInvoiceNumbers || [])

  // Extract from current payments
  const payments = order.payments || []
  payments.forEach((payment) => {
    const invoiceNum = payment?.userAttributes?.freedompayInvoiceNumber
    if (invoiceNum) invoiceNumbers.add(invoiceNum)
  })

  // Extract from neededToBeVoidedAuthPayment (voided payments)
  const voidedPayments = order.neededToBeVoidedAuthPayment || []
  voidedPayments.forEach((payment) => {
    const invoiceNum = payment?.userAttributes?.freedompayInvoiceNumber
    if (invoiceNum) invoiceNumbers.add(invoiceNum)
  })

  // If no invoice numbers found, return order as-is
  if (invoiceNumbers.size === 0) return order

  // Create shallow clone and ensure userAttributes exists
  const updatedOrder = { ...order }
  updatedOrder.userAttributes = updatedOrder.userAttributes || {}

  // Convert Set to sorted array for consistency
  updatedOrder.userAttributes.relatedInvoiceNumbers = Array.from(invoiceNumbers).sort()

  return updatedOrder
}

/**
 * Safe wrapper for accumulateRelatedInvoiceNumbers that handles null/undefined orders
 * and orders without payments.
 *
 * @param {Object} order - The order object to process
 * @returns {Object} Original or updated order
 */
export const ensureRelatedInvoiceNumbers = (order) => {
  if (!order) return order
  if (!order.payments && !order.neededToBeVoidedAuthPayment) return order

  return accumulateRelatedInvoiceNumbers(order)
}

/**
 * Sorts orders by their order dates
 *
 * @param {Array<Object>} orders list of order objects to sort by order dates
 * @param {Boolean} descending whether to sort these orders descending or ascending
 * @returns {Array<Object>} sorted orders in the specified order
 */
export const sortedOrders = (orders, descending = true) => {
  return orderBy(
    orders,
    (o) => {
      return moment(o?.updatedAt || o?.modifiedAt || o?.submittedAt)
    },
    [descending ? 'desc' : 'asc'],
  )
}

/**
 * Rounds an amount (in cents) to the nearest interval.
 * @param {Object} params - The parameters object.
 * @param {number} params.amountInCents - Total amount in cents (integer).
 * @param {number} params.cashRoundingMinimumCurrencyInCents - Rounding interval in cents (e.g. 5, 10, 25, 50).
 * @returns {number} Rounded amount in cents.
 */
export const roundToNearestInterval = ({ amountInCents, cashRoundingMinimumCurrencyInCents = 5 }) => {
  if (!amountInCents) return 0
  return Math.round(amountInCents / cashRoundingMinimumCurrencyInCents) * cashRoundingMinimumCurrencyInCents
}
